import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { siteConditions, type SiteConditionsContent } from '@gg/shared/i18n';
import {
  Container,
  Cta,
  Disclosure,
  Eyebrow,
  H1,
  H2,
  H3,
  Section,
} from '@/components/site/primitives';
import { fill, useSiteContent } from '@/components/site/siteHelpers';
import {
  CheckBoxIcon,
  DocumentIcon,
  DownloadIcon,
  InfoIcon,
  WarningIcon,
} from '@/components/site/siteIcons';
import {
  SITE_EVENTS,
  computeEligibility,
  emptyChecker,
  isPairEvent,
  type CheckerResult,
  type CheckerState,
  type LicenceStatus,
} from '@/lib/eligibility';

/** /conditions: entry requirements by event, eligibility checker, key rules, insurance. */
export function Conditions() {
  const t: SiteConditionsContent = useSiteContent(siteConditions);
  useEffect(() => {
    document.title = t.docTitle;
  }, [t.docTitle]);

  return (
    <>
      <Intro t={t} />
      <Ages t={t} />
      <Documents t={t} />
      <Checker t={t} />
      <KeyRules t={t} />
      <Insurance t={t} />
      <Cancellation t={t} />
      <FullRules t={t} />
    </>
  );
}

type P = { t: SiteConditionsContent };

const rulesLink = 'text-site-clay-deep hover:text-site-ink';

function Intro({ t }: P) {
  return (
    <Section tone="dark" topo className="md:pt-24 md:pb-20">
      <Container>
        <Eyebrow tone="clay">{t.eyebrow}</Eyebrow>
        <H1 className="max-w-[900px] text-[42px] md:text-[68px]">{t.title}</H1>
        <p className="m-0 mt-6 max-w-[760px] text-lg leading-relaxed text-site-cream/85 md:text-[19px]">
          {t.intro}
        </p>
        <nav aria-label={t.jumpLabel} className="mt-9 flex flex-wrap gap-2.5">
          {t.jump.map((j) => (
            <a
              key={j.href}
              href={j.href}
              className="inline-flex min-h-11 items-center border border-site-cream/35 px-4.5 font-site-display text-[13px] tracking-[0.14em] text-site-cream uppercase no-underline hover:border-site-cream hover:text-site-cream"
            >
              {j.label}
            </a>
          ))}
        </nav>
        <p className="m-0 mt-8 flex max-w-[760px] items-start gap-2.5 text-[13px] leading-normal text-site-cream/65">
          <InfoIcon size={16} className="mt-0.5 flex-none text-site-clay-light" />
          <span>{t.draft}</span>
        </p>
      </Container>
    </Section>
  );
}

function Ages({ t }: P) {
  return (
    <Section id="ages">
      <Container>
        <Eyebrow>{t.ages.eyebrow}</Eyebrow>
        <H2 className="md:text-[52px]">{t.ages.title}</H2>
        <p className="m-0 mt-3.5 text-[17px] text-site-ink-muted">{t.ages.sub}</p>
        <div className="mt-10 border border-site-line bg-site-card">
          <div
            aria-hidden="true"
            className="hidden grid-cols-[2.2fr_1fr_0.8fr_2fr] gap-4 bg-site-night px-6 py-3.5 font-site-display text-xs tracking-[0.2em] text-site-cream/80 uppercase lg:grid"
          >
            <span>{t.ages.colEvent}</span>
            <span>{t.ages.colDisc}</span>
            <span>{t.ages.colAge}</span>
            <span>{t.ages.colNote}</span>
          </div>
          <ul className="m-0 list-none p-0">
            {SITE_EVENTS.map((e) => (
              <li
                key={e.id}
                className="grid grid-cols-1 items-center gap-x-4 gap-y-1.5 border-t border-site-line-soft px-6 py-4 first:border-t-0 sm:grid-cols-2 lg:grid-cols-[2.2fr_1fr_0.8fr_2fr] lg:first:border-t"
              >
                <span className="font-site-display text-[19px] font-semibold uppercase">
                  {t.ev[e.id]}
                </span>
                <span className="text-sm text-site-ink-muted">{t.disc[e.disc]}</span>
                <span>
                  <span
                    className={`inline-flex min-h-7.5 items-center px-3 font-site-display text-[15px] font-semibold tracking-[0.06em] ${e.min >= 18 ? 'bg-site-night text-site-cream' : 'bg-site-olive-light text-site-ink'}`}
                  >
                    <span className="sr-only">{t.ages.colAge} : </span>
                    {fill(t.years, { n: e.min })}
                  </span>
                </span>
                <span className="text-sm text-site-ink-muted">{t.ages.notes[e.id]}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-5 flex items-start gap-4 bg-site-clay px-6 py-5.5 text-site-cream">
          <WarningIcon size={24} className="flex-none" />
          <p className="m-0 text-base leading-normal font-medium">{t.ages.minor}</p>
        </div>
      </Container>
    </Section>
  );
}

function Documents({ t }: P) {
  return (
    <Section id="documents" tone="deep">
      <Container>
        <Eyebrow>{t.docs.eyebrow}</Eyebrow>
        <H2 className="md:text-[52px]">{t.docs.title}</H2>
        <p className="m-0 mt-3.5 max-w-[780px] text-[17px] leading-normal text-site-ink-muted">
          {t.docs.sub}
        </p>
        <div className="mt-10 flex flex-col gap-3">
          {t.docs.items.map((d) => (
            <details
              key={d.title}
              open={d.open}
              className="site-details border border-site-line bg-site-card"
            >
              <summary className="flex items-center justify-between gap-4 px-5 py-6 md:px-7">
                <span className="flex flex-col gap-1.5">
                  <span className="font-site-display text-[22px] leading-none font-bold uppercase md:text-[26px]">
                    {d.title}
                  </span>
                  <span className="text-sm text-site-ink-muted">{d.summary}</span>
                </span>
                <span
                  aria-hidden="true"
                  className="site-plus inline-flex size-10 flex-none items-center justify-center border border-site-line-strong text-2xl leading-none text-site-clay transition-transform"
                >
                  +
                </span>
              </summary>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,280px),1fr))] gap-4 px-5 pb-7 md:px-7">
                {d.groups.map((g) => (
                  <div
                    key={g.label}
                    className={`border-t-3 bg-site-ground p-5.5 ${g.minor ? 'border-site-clay' : 'border-site-night'}`}
                  >
                    <p className="m-0 font-site-display text-sm tracking-[0.16em] uppercase">
                      {g.label}
                    </p>
                    <ul className="m-0 mt-3.5 flex flex-col gap-2 pl-4.5 text-[15px] leading-normal text-site-ink-body">
                      {g.items.map((it) => (
                        <li key={it.text}>
                          {it.text}
                          {it.url && (
                            <>
                              {' '}
                              <a href={it.url} className={rulesLink}>
                                {it.urlLabel}
                              </a>
                              {it.after && ` ${it.after}`}
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {d.reserve && (
                  <p className="col-span-full m-0 flex items-start gap-2 text-sm leading-normal text-site-bronze">
                    <InfoIcon size={16} className="mt-0.5 flex-none" />
                    <span>{d.reserve}</span>
                  </p>
                )}
                <p className="col-span-full m-0 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                  <span className="text-site-ink-subtle">{d.ref}</span>
                  <Link to="/reglement" className={rulesLink}>
                    {t.readRules}
                  </Link>
                  <Link to="/#epreuves" className={rulesLink}>
                    {t.seeEvent}
                  </Link>
                </p>
              </div>
            </details>
          ))}
        </div>
      </Container>
    </Section>
  );
}

const fieldLabel =
  'flex items-baseline gap-2.5 font-site-display text-[15px] tracking-[0.12em] uppercase';
const input =
  'mt-2.5 w-full min-h-13 border border-site-line-input bg-site-card px-3.5 font-site text-base text-site-ink focus:outline-2 focus:outline-offset-2 focus:outline-site-clay';

function Num({ n }: { n: number }) {
  return <span className="text-site-clay">{n}</span>;
}

function StatusButtons({
  value,
  onPick,
  lic,
  non,
  label,
}: {
  value: LicenceStatus | '';
  onPick: (s: LicenceStatus) => void;
  lic: string;
  non: string;
  label?: string;
}) {
  const btn = (s: LicenceStatus, text: string) => (
    <button
      type="button"
      aria-pressed={value === s}
      onClick={() => onPick(s)}
      className={`min-h-13 border px-3 font-site-display text-[15px] tracking-[0.1em] uppercase focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-site-clay ${value === s ? 'border-site-night bg-site-night text-site-cream' : 'border-site-line-input bg-site-card text-site-ink hover:border-site-ink'}`}
    >
      {text}
    </button>
  );
  return (
    <div role="group" aria-label={label} className="mt-2.5 grid grid-cols-2 gap-2">
      {btn('lic', lic)}
      {btn('non', non)}
    </div>
  );
}

function Checker({ t }: P) {
  const [s, setS] = useState<CheckerState>(emptyChecker);
  const c = t.check;
  const set = (patch: Partial<CheckerState>) => setS((prev) => ({ ...prev, ...patch }));
  const pair = isPairEvent(s.ev);
  const res = computeEligibility(t, s);

  return (
    <Section id="verifier" tone="dark" topo>
      <Container>
        <Eyebrow tone="clay">{c.eyebrow}</Eyebrow>
        <H2 className="md:text-[52px]">{c.title}</H2>
        <p className="m-0 mt-3.5 text-[17px] text-site-cream/80">{c.sub}</p>

        <div className="mt-10 flex flex-wrap items-stretch gap-5">
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex min-w-0 flex-[1_1_380px] flex-col gap-6.5 bg-site-ground p-5 text-site-ink md:p-8"
          >
            <div>
              <label htmlFor="gg-ev" className={fieldLabel}>
                <Num n={1} />
                {c.q1}
              </label>
              <select
                id="gg-ev"
                value={s.ev}
                onChange={(e) => set({ ev: e.target.value })}
                className={input}
              >
                <option value="">{c.q1ph}</option>
                {SITE_EVENTS.map((e) => (
                  <option key={e.id} value={e.id}>
                    {t.ev[e.id]}
                  </option>
                ))}
              </select>
            </div>

            {!pair ? (
              <>
                <div>
                  <label htmlFor="gg-age" className={fieldLabel}>
                    <Num n={2} />
                    {c.q2}
                  </label>
                  <input
                    id="gg-age"
                    type="number"
                    inputMode="numeric"
                    min={5}
                    max={99}
                    value={s.age}
                    onChange={(e) => set({ age: e.target.value })}
                    placeholder={c.q2ph}
                    className={input}
                  />
                </div>
                <fieldset className="m-0 border-0 p-0">
                  <legend className={`${fieldLabel} p-0`}>
                    <Num n={3} />
                    {c.q3}
                  </legend>
                  <StatusButtons
                    value={s.status}
                    onPick={(status) => set({ status })}
                    lic={c.lic}
                    non={c.non}
                  />
                </fieldset>
              </>
            ) : (
              <>
                <fieldset className="m-0 border-0 p-0">
                  <legend className={`${fieldLabel} p-0`}>
                    <Num n={2} />
                    {c.pair.q2}
                  </legend>
                  <div className="mt-2.5 grid grid-cols-2 gap-2.5">
                    <div>
                      <label
                        htmlFor="gg-age1"
                        className="block text-sm font-semibold text-site-ink-body"
                      >
                        {c.pair.you}
                      </label>
                      <input
                        id="gg-age1"
                        type="number"
                        inputMode="numeric"
                        min={5}
                        max={99}
                        value={s.age}
                        onChange={(e) => set({ age: e.target.value })}
                        placeholder={c.q2ph}
                        className={input}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="gg-age2"
                        className="block text-sm font-semibold text-site-ink-body"
                      >
                        {c.pair.mate}
                      </label>
                      <input
                        id="gg-age2"
                        type="number"
                        inputMode="numeric"
                        min={5}
                        max={99}
                        value={s.age2}
                        onChange={(e) => set({ age2: e.target.value })}
                        placeholder={c.q2ph}
                        className={input}
                      />
                    </div>
                  </div>
                </fieldset>
                <fieldset className="m-0 border-0 p-0">
                  <legend className={`${fieldLabel} p-0`}>
                    <Num n={3} />
                    {c.pair.q3}
                  </legend>
                  <p className="m-0 mt-3 text-sm font-semibold text-site-ink-body">{c.pair.you}</p>
                  <StatusButtons
                    value={s.status}
                    onPick={(status) => set({ status })}
                    lic={c.pair.lic}
                    non={c.pair.non}
                    label={c.pair.you}
                  />
                  <p className="m-0 mt-3.5 text-sm font-semibold text-site-ink-body">
                    {c.pair.mate}
                  </p>
                  <StatusButtons
                    value={s.status2}
                    onPick={(status2) => set({ status2 })}
                    lic={c.pair.lic}
                    non={c.pair.non}
                    label={c.pair.mate}
                  />
                  <p className="m-0 mt-3 text-[13px] leading-normal text-site-ink-muted">
                    {c.pair.hint}
                  </p>
                </fieldset>
              </>
            )}

            <button
              type="button"
              onClick={() => setS(emptyChecker)}
              className="min-h-11 self-start border-0 bg-transparent p-0 text-sm text-site-ink-muted underline hover:text-site-ink"
            >
              {c.reset}
            </button>
          </form>

          <div
            role="status"
            aria-live="polite"
            className="min-w-0 flex-[1.3_1_440px] border border-site-cream/20 bg-site-night-deep/70 p-5 md:p-8"
          >
            {res ? (
              <CheckerAnswer t={t} res={res} />
            ) : (
              <div className="flex h-full min-h-[300px] flex-col items-start justify-center gap-3.5">
                <CheckBoxIcon size={40} className="text-site-clay-light" />
                <p className="m-0 text-lg leading-normal text-site-cream/80">{c.empty}</p>
              </div>
            )}
          </div>
        </div>
        <p className="m-0 mt-4.5 text-[13px] text-site-cream/60">{c.disclaimer}</p>
      </Container>
    </Section>
  );
}

function AnswerBlock({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="border-t border-site-cream/15 pt-5">
      <p className="m-0 font-site-display text-sm tracking-[0.16em] text-site-clay-light uppercase">
        {title}
      </p>
      {children}
    </div>
  );
}

function List({ items, className = '' }: { items: readonly string[]; className?: string }) {
  return (
    <ul className={`m-0 mt-2.5 flex flex-col gap-2 pl-4.5 text-[15px] leading-normal ${className}`}>
      {items.map((x) => (
        <li key={x}>{x}</li>
      ))}
    </ul>
  );
}

function MinorBox({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="bg-site-clay px-5 py-4.5 text-site-cream">
      <p className="m-0 font-site-display text-sm tracking-[0.16em] uppercase">{title}</p>
      <List items={items} />
    </div>
  );
}

function CheckerAnswer({ t, res }: P & { res: CheckerResult }) {
  const c = t.check;
  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="m-0 font-site-display text-[13px] tracking-[0.2em] text-site-cream/60 uppercase">
          {res.evName}
        </p>
        <div className="mt-3 flex items-start gap-3.5">
          <span
            aria-hidden="true"
            className={`inline-flex size-10 flex-none items-center justify-center font-site-display text-[22px] font-bold text-site-ink ${res.eligible ? 'bg-site-olive-light' : 'bg-site-clay-pale'}`}
          >
            {res.eligible ? '✓' : '×'}
          </span>
          <div>
            <p className="m-0 font-site-display text-[26px] leading-tight font-bold text-site-cream uppercase">
              {res.headline}
            </p>
            <p className="m-0 mt-1.5 text-[15px] leading-normal text-site-cream/80">{res.ageMsg}</p>
          </div>
        </div>
      </div>

      {!res.eligible && res.alts.length > 0 && (
        <div>
          <p className="m-0 font-site-display text-sm tracking-[0.16em] text-site-clay-light uppercase">
            {res.isPair ? c.pair.altTitle : c.altTitle}
          </p>
          <List items={res.alts} className="text-site-cream/90" />
        </div>
      )}

      {res.eligible && (
        <div className="flex flex-col gap-5.5">
          {!res.isPair && (
            <AnswerBlock title={c.docsTitle}>
              {res.docs.length > 0 && <List items={res.docs} className="text-site-cream/90" />}
              {res.noDocsMsg && (
                <p className="m-0 mt-2.5 text-[15px] leading-normal text-site-cream/90">
                  {res.noDocsMsg}
                </p>
              )}
              {res.showPps && (
                <p className="m-0 mt-2.5 text-sm">
                  <a
                    href="https://pps.athle.fr"
                    className="text-site-clay-pale hover:text-site-cream"
                  >
                    {c.ppsLink}
                  </a>
                </p>
              )}
              {res.showFfcHealth && (
                <p className="m-0 mt-2.5 text-sm">
                  <a href={c.ffcHealthUrl} className="text-site-clay-pale hover:text-site-cream">
                    {c.ffcHealthLink}
                  </a>
                </p>
              )}
            </AnswerBlock>
          )}
          {res.isPair && (
            <AnswerBlock title={c.pair.docsTitle}>
              <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(min(100%,200px),1fr))] gap-2.5">
                {res.members.map((m) => (
                  <div key={m.who} className="border border-site-cream/20 bg-site-cream/5 p-4.5">
                    <p className="m-0 font-site-display text-lg font-bold text-site-cream uppercase">
                      {m.who}
                    </p>
                    <p className="m-0 mt-1 text-[13px] text-site-cream/65">{m.statusLabel}</p>
                    <List items={m.docs} className="text-site-cream/90" />
                    {m.licExp && (
                      <p className="m-0 mt-2.5 text-sm">
                        <a href={c.licExpUrl} className="text-site-clay-pale hover:text-site-cream">
                          {c.licExpLink}
                        </a>
                      </p>
                    )}
                    {m.minor.length > 0 && (
                      <div className="mt-3.5">
                        <MinorBox title={c.minorTitle} items={m.minor} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </AnswerBlock>
          )}
          {res.minor.length > 0 && <MinorBox title={c.minorTitle} items={res.minor} />}
          <div>
            <p className="m-0 font-site-display text-sm tracking-[0.16em] text-site-cream/60 uppercase">
              {c.notesTitle}
            </p>
            <List items={res.notes} className="text-sm text-site-cream/75" />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 border-t border-site-cream/15 pt-5">
        {res.canRegister && (
          <Cta to="/formats" size="md">
            {c.register}
          </Cta>
        )}
        {res.soon && (
          <span className="inline-flex min-h-12.5 items-center border border-site-cream/40 px-5.5 font-site-display text-sm tracking-[0.14em] text-site-cream/85 uppercase">
            {c.soon}
          </span>
        )}
        <Link to="/reglement" className="text-sm text-site-cream hover:text-site-clay-light">
          {t.readRules}
        </Link>
      </div>
    </div>
  );
}

function KeyRules({ t }: P) {
  return (
    <Section id="regles">
      <Container>
        <Eyebrow>{t.rules.eyebrow}</Eyebrow>
        <H2 className="md:text-[52px]">{t.rules.title}</H2>
        <p className="m-0 mt-3.5 max-w-[820px] text-[17px] leading-normal text-site-ink-body">
          {t.rules.sub}
        </p>
        <div className="mt-10 grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-4">
          {t.rules.items.map((r, i) => (
            <article
              key={r.title}
              className={`flex flex-col gap-3 border p-7 ${r.dark ? 'border-site-night bg-site-night text-site-cream' : 'border-site-line bg-site-card text-site-ink'}`}
            >
              <span className="font-site-display text-3xl leading-none font-bold text-site-clay">
                {String(i + 1).padStart(2, '0')}
              </span>
              <H3 className="text-[22px]">{r.title}</H3>
              <p
                className={`m-0 flex-1 text-[15px] leading-normal ${r.dark ? 'text-site-cream/80' : 'text-site-ink-muted'}`}
              >
                {r.text}
              </p>
              <p className="m-0 mt-1.5 flex flex-wrap gap-x-3.5 gap-y-1.5 text-[13px]">
                <span className={r.dark ? 'text-site-cream/80' : 'text-site-ink-muted'}>
                  {r.ref}
                </span>
                <Link
                  to="/reglement"
                  className={r.dark ? 'text-site-clay-pale hover:text-site-cream' : rulesLink}
                >
                  {t.readDetail}
                </Link>
              </p>
            </article>
          ))}
        </div>
      </Container>
    </Section>
  );
}

const insKinds = {
  org: { accent: 'border-t-site-night', tag: 'bg-site-night text-site-cream' },
  rec: { accent: 'border-t-site-clay', tag: 'bg-site-clay text-site-cream' },
  you: { accent: 'border-t-site-olive', tag: 'bg-site-olive-light text-site-ink' },
} as const;

function Insurance({ t }: P) {
  return (
    <Section id="assurances" tone="deep">
      <Container>
        <Eyebrow>{t.ins.eyebrow}</Eyebrow>
        <H2 className="md:text-[52px]">{t.ins.title}</H2>
        <p className="m-0 mt-3.5 max-w-[820px] text-[17px] leading-normal text-site-ink-muted">
          {t.ins.sub}
        </p>
        <div className="mt-10 grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-4">
          {t.ins.items.map((it) => (
            <article
              key={it.title}
              className={`flex flex-col gap-3 border border-t-4 border-site-line bg-site-card p-7.5 ${insKinds[it.kind].accent}`}
            >
              <span
                className={`inline-flex min-h-7 items-center self-start px-2.5 font-site-display text-xs font-semibold tracking-[0.14em] uppercase ${insKinds[it.kind].tag}`}
              >
                {it.tag}
              </span>
              <H3 className="text-2xl">{it.title}</H3>
              <p className="m-0 text-[15px] leading-relaxed text-site-ink-body">{it.text}</p>
              {it.points && it.points.length > 0 && (
                <ul className="m-0 flex flex-col gap-1.5 pl-4.5 text-[15px] leading-snug text-site-ink-body">
                  {it.points.map((pt) => (
                    <li key={pt}>{pt}</li>
                  ))}
                </ul>
              )}
              {it.foot && (
                <p className="m-0 mt-1 text-sm leading-normal text-site-ink-muted">{it.foot}</p>
              )}
            </article>
          ))}
        </div>
        {t.ins.reserve && (
          <p className="m-0 mt-5 flex max-w-[900px] items-start gap-2.5 text-sm leading-normal text-site-bronze">
            <InfoIcon size={16} className="mt-0.5 flex-none" />
            <span>{t.ins.reserve}</span>
          </p>
        )}
        <p className="m-0 mt-3.5 text-sm">
          <Link to="/reglement#art-16" className={rulesLink}>
            {t.ins.link}
          </Link>
        </p>
      </Container>
    </Section>
  );
}

function Cancellation({ t }: P) {
  return (
    <Section id="annulation" tone="dark" topo>
      <Container>
        <Eyebrow tone="clay">{t.cancel.eyebrow}</Eyebrow>
        <H2 className="max-w-[900px] md:text-[52px]">{t.cancel.title}</H2>
        <p className="m-0 mt-5.5 inline-flex items-center gap-2.5 border border-site-olive-light/60 px-4 py-2.5 text-sm leading-snug">
          <span aria-hidden="true" className="size-2 flex-none rounded-full bg-site-olive-light" />
          <span>{t.cancel.status}</span>
        </p>
        <div className="mt-10 flex flex-wrap items-start gap-10">
          <div className="min-w-0 flex-[1_1_420px]">
            <p className="m-0 mb-1.5 font-site-display text-sm tracking-[0.16em] text-site-cream/65 uppercase">
              {t.cancel.keyTitle}
            </p>
            <ol className="m-0 list-none p-0">
              {t.cancel.points.map((k, i) => (
                <li key={k} className="flex gap-4 border-b border-site-cream/15 py-4">
                  <span className="min-w-7 flex-none font-site-display text-xl leading-tight font-bold text-site-clay-light">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base leading-normal text-site-cream/90">{k}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="min-w-0 flex-[1_1_460px]">
            <p className="m-0 mb-1.5 font-site-display text-sm tracking-[0.16em] text-site-cream/65 uppercase">
              {t.cancel.faqTitle}
            </p>
            <div className="border-t border-site-cream/20">
              {t.cancel.faq.map((q) => (
                <Disclosure key={q.q} summary={q.q} dark>
                  <p className="m-0 text-[15px]">{q.a}</p>
                </Disclosure>
              ))}
            </div>
          </div>
        </div>
        <p className="m-0 mt-7 text-sm">
          <Link to="/reglement#art-17" className="text-site-clay-pale hover:text-site-cream">
            {t.cancel.link}
          </Link>
        </p>
      </Container>
    </Section>
  );
}

function FullRules({ t }: P) {
  return (
    <Section id="reglement" className="pt-0! md:pt-0!">
      <Container className="border-t border-site-line-strong pt-16">
        <Eyebrow>{t.full.eyebrow}</Eyebrow>
        <H2 className="md:text-[52px]">{t.full.title}</H2>
        <p className="m-0 mt-3.5 max-w-[820px] text-[17px] leading-normal text-site-ink-muted">
          {t.full.sub}
        </p>
        <div className="mt-9 grid grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-4">
          {t.full.docs.map((f) => (
            <div
              key={f.code}
              className="flex flex-col gap-3.5 border border-site-line bg-site-card p-7.5"
            >
              <div className="flex items-center gap-3.5">
                <DocumentIcon size={32} className="text-site-clay" />
                <div>
                  <p className="m-0 font-site-display text-[22px] font-bold uppercase">{f.title}</p>
                  <p className="m-0 mt-1 text-sm text-site-ink-muted">{f.lang}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2.5">
                <ReadOnline code={f.code} label={t.full.readOnline} />
                <Cta
                  href={f.pdf}
                  download={f.file}
                  variant="outline"
                  size="md"
                  className="min-h-12"
                >
                  <DownloadIcon size={16} />
                  {t.full.download}
                </Cta>
              </div>
            </div>
          ))}
        </div>
        <p className="m-0 mt-4.5 text-[13px] text-site-ink-subtle">{t.full.version}</p>
      </Container>
    </Section>
  );
}

/** "Read online" opens /reglement in that document's language. */
function ReadOnline({ code, label }: { code: 'fr' | 'en'; label: string }) {
  return (
    <Cta
      to={`/reglement?lang=${code}`}
      hrefLang={code}
      variant="night"
      size="md"
      className="min-h-12"
    >
      {label}
    </Cta>
  );
}
